import { useState, useEffect } from 'react';
import { User } from 'services/auth/auth.types';
import { useUpdateUserMutation, useGetAllUsersMutation, useCreateUserMutation } from 'services/user/user.endpoint';
import { CreateUserPayload, UpdateUserPayload } from 'services/user/user.types';
import { useApiToast } from 'components/Toaster';
export const useUsersDirectory = () => {
    const [users, setUsers] = useState<Array<User>>([]);
    const [lastUserCreated, setLastUserCreated] = useState<CreateUserPayload | null>(null);
    const [userToCreate, setUserToCreate] = useState<CreateUserPayload | null>(null);
    const [search, setSearch] = useState('');
    const [isCreateUserModalOpen, setIsCreateUserModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
    const [lastUserUpdated, setLastUserUpdated] = useState<UpdateUserPayload | null>(null);
    const [selectedUser, setSelectedUser] = useState<UpdateUserPayload | null>(null);
    const { showApiToast, showErrorToast } = useApiToast();
    const [
        updateUser,
        {
            isLoading: isUpdateUserLoading,
            isError: isUpdateUserError,
            isSuccess: isUpdateUserSuccess,
            error: updateUserError,
            data: updateUserData
        }
    ] = useUpdateUserMutation();
    const [
        getAllUsers,
        {
            isLoading: isGetAllUsersLoading,
            isError: isGetAllUsersError,
            isSuccess: isGetAllUsersSuccess,
            error: getAllUsersError,
            data: getAllUsersData
        }
    ] = useGetAllUsersMutation();
    const [
        createUser,
        {
            isLoading: isCreateUserLoading,
            isError: isCreateUserError,
            isSuccess: isCreateUserSuccess,
            error: createUserError,
            data: createUserData
        }
    ] = useCreateUserMutation();

    const handleUpdateUser = async (data: UpdateUserPayload) => {
        if (!data) return;
        setLastUserUpdated(data);
        await updateUser(data);
    };

    const handleCreateUser = async (data: CreateUserPayload) => {
        if (!data) return;
        setLastUserCreated(data);
        const response = await createUser(data);
        if (response.data) {
            await updateUserState();
        }
    };

    const handleUserStatusChange = async (id: number) => {
        const userToUpdate = users.find((user) => user.id === id);
        if (!userToUpdate) return;
        const userPayload = {
            id: userToUpdate.id,
            firstname: userToUpdate.firstname,
            lastname: userToUpdate.lastname,
            email: userToUpdate.email,
            phone: userToUpdate.phone,
            color: userToUpdate.color,
            role: userToUpdate.role,
            online: !userToUpdate.online,
            departments: userToUpdate.departments
        };
        setLastUserUpdated(userPayload);
        const response = await updateUser(userPayload);
        if (response.data) {
            await updateUserState();
        }
    };

    const updateUserState = async () => {
        const usersResponse = await getAllUsers(undefined).unwrap();
        setUsers(usersResponse.data || []);
    };

    useEffect(() => {
        getAllUsers(undefined);
    }, []);

    useEffect(() => {
        if (isUpdateUserLoading) return;
        if (isUpdateUserError && updateUserError) {
            showErrorToast(updateUserError, {
                message: `Erreur lors de la mise à jour de l'utilisateur "${lastUserUpdated?.lastname} ${lastUserUpdated?.firstname}"`
            });
            return;
        }
        if (isUpdateUserSuccess && updateUserData) {
            showApiToast(updateUserData, {
                successMessage: `Utilisateur "${lastUserUpdated?.lastname} ${lastUserUpdated?.firstname}" mis à jour avec succès`,
                errorMessage: `Erreur lors de la mise à jour de l'utilisateur "${lastUserUpdated?.lastname} ${lastUserUpdated?.firstname}"`
            });
            return;
        }
    }, [isUpdateUserLoading, isUpdateUserError, isUpdateUserSuccess, updateUserError, updateUserData]);

    useEffect(() => {
        if (isGetAllUsersLoading) return;
        if (isGetAllUsersError && getAllUsersError) {
            showErrorToast(getAllUsersError, {
                message: 'Erreur lors de la récupération des utilisateurs'
            });
            return;
        }
        if (isGetAllUsersSuccess && getAllUsersData) {
            setUsers(getAllUsersData.data);
            return;
        }
    }, [isGetAllUsersLoading, isGetAllUsersError, isGetAllUsersSuccess, getAllUsersError, getAllUsersData]);

    useEffect(() => {
        if (isCreateUserLoading) return;
        if (isCreateUserError && createUserError) {
            showErrorToast(createUserError, {
                message: `Erreur lors de la création de l'utilisateur "${lastUserCreated?.lastname} ${lastUserCreated?.firstname}"`
            });
            return;
        }
        if (isCreateUserSuccess && createUserData) {
            showApiToast(createUserData, {
                successMessage: `Utilisateur "${lastUserCreated?.lastname} ${lastUserCreated?.firstname}" créé avec succès`,
                errorMessage: `Erreur lors de la création de l'utilisateur "${lastUserCreated?.lastname} ${lastUserCreated?.firstname}"`
            });
            return;
        }
    }, [isCreateUserLoading, isCreateUserError, isCreateUserSuccess, createUserError, createUserData]);

    return {
        isGetAllUsersLoading,
        handleUpdateUser,
        handleUserStatusChange,
        users,
        search,
        setSearch,
        isCreateUserModalOpen,
        setIsCreateUserModalOpen,
        userToCreate,
        setUserToCreate,
        handleCreateUser,
        modalMode,
        setModalMode,
        selectedUser,
        setSelectedUser
    };
};
